import type { JSCodeshift, JSXElement, JSXExpressionContainer } from 'jscodeshift';

/**
 * Process children of a JSX element to detect icon components and add iconStart or iconEnd attributes accordingly.
 * This is specific to icon handling but can be reused in codemods dealing with icon children.
 */
const processIconChildren = (
  j: JSCodeshift,
  children: (JSXElement | JSXExpressionContainer | unknown)[] | undefined,
  iconImports: Set<string>,
  openingElement: JSXElement['openingElement'],
) => {
  if (!children || !openingElement.attributes) return;

  const unwrapJsxElement = (node: unknown): JSXElement | unknown => {
    if (
      typeof node === 'object' &&
      node !== null &&
      'type' in node &&
      node.type === 'JSXExpressionContainer' &&
      j.JSXElement.check((node as JSXExpressionContainer).expression)
    ) {
      return (node as JSXExpressionContainer).expression;
    }
    return node;
  };

  const totalChildren = children.length;

  // Find index of icon child
  const iconChildIndex = children.findIndex((child) => {
    const unwrapped = unwrapJsxElement(child);
    return (
      j.JSXElement.check(unwrapped) &&
      unwrapped.openingElement.name.type === 'JSXIdentifier' &&
      iconImports.has(unwrapped.openingElement.name.name)
    );
  });

  if (iconChildIndex === -1) return;

  const iconChild = unwrapJsxElement(children[iconChildIndex]) as JSXElement;

  if (iconChild?.openingElement.name.type !== 'JSXIdentifier') return;

  const iconName = iconChild.openingElement.name.name;

  // Determine if icon is closer to start or end
  const distanceToStart = iconChildIndex;
  const distanceToEnd = totalChildren - 1 - iconChildIndex;
  const iconPropName = distanceToStart <= distanceToEnd ? 'addonStart' : 'addonEnd';

  // Build: { type: 'icon', value: <IconName /> }
  const iconObject = j.objectExpression([
    j.property('init', j.identifier('type'), j.literal('icon')),
    j.property('init', j.identifier('value'), iconChild),
  ]);
  const iconProp = j.jsxAttribute(
    j.jsxIdentifier(iconPropName),
    j.jsxExpressionContainer(iconObject),
  );

  openingElement.attributes.push(iconProp);

  // Remove the icon child
  children.splice(iconChildIndex, 1);

  // Helper to check if a child is whitespace-only JSXText
  const isWhitespaceJsxText = (node: unknown): boolean => {
    return (
      typeof node === 'object' &&
      node !== null &&
      (node as { type?: unknown }).type === 'JSXText' &&
      typeof (node as { value?: string }).value === 'string' &&
      (node as { value?: string }).value!.trim() === ''
    );
  };

  // Remove adjacent whitespace-only JSXText node if any
  if (iconChildIndex - 1 >= 0 && isWhitespaceJsxText(children[iconChildIndex - 1])) {
    children.splice(iconChildIndex - 1, 1);
  } else if (isWhitespaceJsxText(children[iconChildIndex])) {
    children.splice(iconChildIndex, 1);
  }
};

export default processIconChildren;
